import {Component, OnDestroy, OnInit, QueryList, ViewChildren} from '@angular/core';
import {MachineCategory} from '@models/adminstration/portal-setup/machine-category/machine-category';
import {MachineCategoryService} from '@services/administration/portal-setup/machine-category/machine-category.service';
import {MachineService} from '@services/administration/machine/machine.service';
import {
  MachineCategoryEditComponent
} from '@components/administration/portal-setup/portal-setup-machine-categories/modal/machine-category-edit/machine-category-edit.component';
import {ApiLocationService} from '@services/api-location/api-location.service';
import {LoadingIndicatorService} from '@services/loading-indicator/loading-indicator.service';
import {NotificationService} from '@services/notification/notification.service';
import {TranslateService} from '@ngx-translate/core';
import {SortEvent, TableHeaderSortableDirective} from '@shared/table-header-sortable/table-header-sortable.directive';
import {TableHeaderSortableService} from '@services/table-header-sortable/table-header-sortable.service';
import {
  MachineCategoryColumnConfiguration
} from '@models/adminstration/portal-setup/machine-category/machine-category-column-configuration';
import {CategoryColumn} from '@models/adminstration/portal-setup/machine-category/category-column';
import {
  MachineCategoryBaseColumnTypes
} from '@constants/administration/portal-setup/machine-category/machine-category-base-column-types';
import {ModalService} from '@services/modal/modal.service';
import {Subject} from 'rxjs';
import {FirebaseAuthenticationService} from '@services/firebase/auth/firebase-authentication.service';
import {switchMap, takeUntil} from 'rxjs/operators';
import {User} from '@models/user/user';
import {Role} from '@models/user/role';

@Component({
    selector: 'scitis-portal-setup-machine-categories',
    templateUrl: './portal-setup-machine-categories.component.html',
    styleUrls: ['./portal-setup-machine-categories.component.scss'],
    standalone: false
})
export class PortalSetupMachineCategoriesComponent implements OnInit, OnDestroy {
  public pageTitle: string = '';
  public isSuperAdminRole: boolean = false;
  public machineCategoryList: MachineCategory[] = [];
  public searchTerm: string = '';
  @ViewChildren(TableHeaderSortableDirective) sortableHeaders: QueryList<TableHeaderSortableDirective> = new QueryList<TableHeaderSortableDirective>();
  private $destroy: Subject<boolean> = new Subject<boolean>();
  private readonly BASE_URL: string;
  private readonly addCategoryPageTitle: string;

  constructor(private machineCategoryService: MachineCategoryService,
              private firebaseAuthenticationService: FirebaseAuthenticationService,
              private machineService: MachineService,
              private apiLocationService: ApiLocationService,
              private loadingIndicator: LoadingIndicatorService,
              private translate: TranslateService,
              private notification: NotificationService,
              private tableHeaderSortableService: TableHeaderSortableService,
              private modalService: ModalService) {
    this.BASE_URL = this.apiLocationService.getLocation();
    this.pageTitle = this.translate.instant('Machine categories');
    this.addCategoryPageTitle = this.translate.instant('New Category');
  }

  ngOnInit(): void {
    this.setUserDetails();
  }

  ngOnDestroy(): void {
    this.$destroy.next(true);
    this.$destroy.complete();
  }

  public onColumnSort({column, direction}: SortEvent): void {
    this.sortableHeaders = this.tableHeaderSortableService.resetHeaders(this.sortableHeaders, column);
    this.machineCategoryList = this.tableHeaderSortableService.sortItems<MachineCategory>(this.machineCategoryList, column, direction);
  }

  public onAddCategory() {
    const modalRef = this.modalService.open(MachineCategoryEditComponent, {size: 'xl'});
    modalRef.componentInstance.pageTitle = this.addCategoryPageTitle;
    modalRef.componentInstance.existingCategoryNames = this.machineCategoryList.map(({name}) => name);
    modalRef.result.then((result) => {
      this.createCategory(result.config);
      const successMessage = this.translate.instant(`The category ${result.category.name} has been successfully created.`);
      this.notification.success(successMessage, 'Machine Categories', false);
    });
  }

  public onImportCategories() {
    this.loadingIndicator.show();
    this.machineService.listCategories().pipe(takeUntil(this.$destroy)).subscribe({
      next: (categories: string[]) => {
        const skippedCategories: string[] = [];
        const failedCategories: string[] = [];
        const successfulCategories: string[] = [];
        const promises: Promise<void | MachineCategoryColumnConfiguration>[] = [];

        for (const category of categories) {
          if (this.doesMachineCategoryAlreadyExist(category)) {
            skippedCategories.push(category);
            continue;
          }

          const columnConfig = new MachineCategoryColumnConfiguration();
          this.setDefaultColumn(columnConfig);
          columnConfig.categoryName = category;
          columnConfig.categoryDisplayName = category;
          const iconPath = './assets/img/machine_type/' + category + '.png';
          promises.push(this.createPromise(iconPath, category, columnConfig, successfulCategories, failedCategories));
        }

        Promise.all(promises).then((result) => {
          result.forEach(item => {
            if (item) {
              this.createCategory(item);
            }
          });
        }).finally(() => {
              this.showNotification(skippedCategories, failedCategories, successfulCategories);
        });
        this.loadingIndicator.hide();
      },
      error: (error) => {
        this.loadingIndicator.hide();
        const message = this.translate.instant('Error getting machine categories') + ': ' + error;
        this.notification.error(message, error, 'Machine Categories', false);
      }
    });
  }

  public onDeleteMachineCategory(machineCategory: MachineCategory) {
    if (!machineCategory || !machineCategory.id) {
      return;
    }

    this.loadingIndicator.show();
    this.machineCategoryService.deleteMachineCategory(machineCategory.id).pipe(takeUntil(this.$destroy)).subscribe({
      next: () => {
        const index = this.machineCategoryList.indexOf(machineCategory);
        if (index >= 0) {
          this.machineCategoryList.splice(index, 1);
        }
        let message = this.translate.instant('Machine category "#0" has been deleted');
        message = message.replace('#0', machineCategory.name);
        this.notification.success(message, 'Machine Categories', false);
        this.loadingIndicator.hide();
        },
      error: (reason) => {
        console.log('Deleting machine category was not successfull: ' + reason);
        let message = this.translate.instant('Error deleting machine category "#0"');
        message = message.replace('#0', machineCategory.name);
        this.notification.error(message, reason, 'Machine Categories', false);
        this.loadingIndicator.hide();
      }
    });
  }

  public onMachineCategoryUpdated(columnConfig: MachineCategoryColumnConfiguration) {
    this.saveCategory(columnConfig);
  }

  private showNotification(skippedCategories, failedCategories, successfulCategories) {
    if (skippedCategories.length) {
      let warningMessage = this.translate.instant('The following categories were skipped because they already exist: ');
      warningMessage = warningMessage + skippedCategories.join(', ');
      this.notification.warning(warningMessage, 'Machine Categories');
    }

    if (failedCategories.length) {
      let errorMessage = this.translate.instant('The following categories failed to import: ');
      errorMessage = errorMessage + failedCategories.join(', ');
      this.notification.warning(errorMessage, 'Machine Categories');
    }

    if (successfulCategories.length) {
      let successMessage = this.translate.instant('The following categories were imported: ');
      successMessage = successMessage + successfulCategories.join(', ');
      this.notification.success(successMessage, 'Machine Categories', false);
    }
  }

  private async createPromise(iconPath: string, category: string, columnConfig: MachineCategoryColumnConfiguration,
                              successfulCategories: string[], failedCategories: string[]): Promise<MachineCategoryColumnConfiguration | void> {
    return fetch(iconPath)
    .then(response => {
      if (response && response.status === 200) {
        return response.blob();
      }
      return Promise.reject('response');
    }).catch(() => {
      console.log('Error fetching response data from icon path ' + iconPath);
    }).then(data => {
      if (data) {
        return this.blobToBase64(data);
      }
      return Promise.reject('data');
    }).catch((error) => {
      console.log('Error converting response data to blob: ', error);
    }).then(result => {
      if (result) {
        columnConfig.categoryIcon = String(result);
        successfulCategories.push(category);
        return columnConfig;
      }
      return Promise.reject('result');
    }).catch((error) => {
      console.log('Error converting blob to base64 string: ', error);
      failedCategories.push(category);
    });
  }

  private setUserDetails(): void {
    this.loadingIndicator.show();
    this.firebaseAuthenticationService.getLoggedUserDetails()
      .pipe(
        switchMap((user: User) => {
          this.setAuthenticatedUserRoleAndOrganization(user);
          return this.machineCategoryService.listMachineCategories();
        }),
        takeUntil(this.$destroy))
      .subscribe({
        next: (machineCategories) => {
          this.machineCategoryList = machineCategories;
          this.loadingIndicator.hide();
        },
        error: (error) => {
          console.log(error);
          this.loadingIndicator.hide();
        }
      });
  }

  private setAuthenticatedUserRoleAndOrganization(user: User): void {
    if (user.parentId) {
      const userRole = new Role(user);
      this.isSuperAdminRole = userRole.isSuperAdminRole;
    }
  }

  private createCategory(columnConfig: MachineCategoryColumnConfiguration): void {
    this.loadingIndicator.show();
    this.machineCategoryService.createMachineCategory(columnConfig).pipe(takeUntil(this.$destroy)).subscribe({
      next: (success) => {
        this.machineCategoryList = [...this.machineCategoryList, success];
        this.loadingIndicator.hide();
      }, error: (error) => {
        this.notification.error(this.translate.instant('Error saving machine category') + ' ' + columnConfig.id,
          error, 'Machine Categories', false);
        this.loadingIndicator.hide();
      }
    });
  }

  private saveCategory(columnConfig: MachineCategoryColumnConfiguration) {
    this.loadingIndicator.show();
    this.machineCategoryService.saveMachineCategory(columnConfig).pipe(takeUntil(this.$destroy)).subscribe({
      next: () => {
        this.loadingIndicator.hide();
        this.notification.success(this.translate.instant('Successfully saved machine category') + ' ' + columnConfig.categoryName,
          'Machine Categories', false);
        },
      error: (error) => {
        this.notification.error(this.translate.instant('Error saving machine category') + ' ' + columnConfig.id,
          error, 'Machine Categories', false);
        this.loadingIndicator.hide();
      }
    });
  }

  private blobToBase64(blob) {
    return new Promise((resolve, _) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
  }

  private doesMachineCategoryAlreadyExist(categoryName: string): boolean {
    return this.machineCategoryList.some((obj) => obj.name === categoryName);
  }

  private setDefaultColumn(columnConfig: MachineCategoryColumnConfiguration) {
    const defaultColumn = new CategoryColumn();
    defaultColumn.deletable = false;
    defaultColumn.type = MachineCategoryBaseColumnTypes.DEFAULT;
    defaultColumn.name = 'machineName';
    defaultColumn.label = 'Machine name';
    columnConfig.categoryColumns.push(defaultColumn);
  }
}
